import {
  Box,
  Flex,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useUser } from '@clerk/clerk-react';
import AntecedentesCompactPanel from 'components/layouts/patients/AntecedentesCompactPanel';
import AlergiasSummaryPanel from 'components/layouts/patients/AlergiasSummaryPanel';
import PatientCard from 'components/layouts/patients/PatientCard';
import PatientModal from 'components/layouts/patients/PatientModal';
import PatientTabs from 'components/layouts/patients/PatientTabs';
import React from 'react';
import { MdArrowBack } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useAntecedentesResumen } from 'hooks/useAntecedentes';
import { usePatient, useUpdatePatient } from 'hooks/usePatients';
import { useTarjetero } from 'hooks/useTarjetero';
import { CreatePatientData } from 'types/models';
import { getUserRole } from 'utils/auth';

export default function PatientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const role = getUserRole(user) ?? '';
  const isAdmin = role === 'admin';
  const canWrite = ['admin', 'medico', 'fisioterapeuta'].includes(role);

  // Fire all queries in parallel — tarjetero no longer waits for patient
  const { data: patient, isLoading, error } = usePatient(id!);
  const { data: resumen } = useAntecedentesResumen(id!);
  useTarjetero(id!);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const updatePatient = useUpdatePatient();

  const handleSave = async (data: CreatePatientData): Promise<void> => {
    await updatePatient.mutateAsync({ id: patient!.id, payload: data });
  };

  if (isLoading) {
    return (
      <Flex minH='60vh' align='center' justify='center'>
        <Spinner size='xl' color='brand.500' thickness='3px' />
      </Flex>
    );
  }

  if (error || !patient) {
    return (
      <Flex minH='60vh' align='center' justify='center' direction='column' gap='4'>
        <Text color='red.400' fontWeight='600' fontSize='md'>
          No se pudo cargar el paciente.
        </Text>
        <IconButton
          aria-label='Volver'
          icon={<Icon as={MdArrowBack} />}
          colorScheme='brand'
          variant='ghost'
          onClick={() => navigate('/admin/patients')}
        />
      </Flex>
    );
  }

  return (
    <Box minH='100vh' pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Breadcrumb + back */}
      <Flex align='center' gap='2' mb='24px'>
        <IconButton
          aria-label='Volver a pacientes'
          icon={<Icon as={MdArrowBack} />}
          variant='ghost'
          size='sm'
          colorScheme='brand'
          onClick={() => navigate('/admin/patients')}
        />
        <Text color='secondaryGray.600' fontSize='sm'>
          Pacientes /{' '}
          <Text as='span' fontWeight='700' color='secondaryGray.900'>
            {patient.nombres} {patient.apellidos}
          </Text>
        </Text>
      </Flex>

      <Flex direction='column' gap='20px'>
        {/* Row 1: datos + antecedentes + alergias — single card */}
        <Card p='0' overflow='hidden'>
          <Grid
            templateColumns={{ base: '1fr', lg: '1.5fr 1fr', xl: '2fr 1.5fr 1.5fr' }}>
            <GridItem>
              <PatientCard
                patient={patient}
                onEdit={onOpen}
                bg='transparent'
                boxShadow='none'
                borderRadius='0'
              />
            </GridItem>
            <GridItem>
              <AntecedentesCompactPanel
                patient={patient}
                resumen={resumen}
                bg='transparent'
                boxShadow='none'
                borderRadius='0'
              />
            </GridItem>
            <GridItem>
              <AlergiasSummaryPanel
                patient={patient}
                bg='transparent'
                boxShadow='none'
                borderRadius='0'
              />
            </GridItem>
          </Grid>
        </Card>

        {/* Row 2: tabs */}
        <PatientTabs patient={patient} />
      </Flex>

      <PatientModal
        isOpen={isOpen}
        onClose={onClose}
        onSave={handleSave}
        patient={patient}
      />
    </Box>
  );
}
